import { useEffect, useMemo, useRef, useState } from "react";
import intl from "react-intl-universal";
import qs from "qs";
import { TbBuildingBank } from "react-icons/tb";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useBaasCapabilities } from "../../../../Hooks/useBaasCapabilities";
import { useBaasPlatformCreateAccess } from "../../../../Hooks/useBaasPlatformCreateAccess";
import { useBaasPageLoading } from "../../../../Hooks/useBaasPageLoading";
import { BaasEntidadesPageSkeleton } from "../components/BaasSkeleton";
import { useToast } from "../../../../Hooks/toast";
import { GridHistoryCard, GridPagination } from "../../../../Components/Molecules/GridHistory";
import { BaasLoggedInDot, BaasRowActionMenu } from "../components/BaasRowActionMenu";
import { paginateList } from "../../Extract/extractFilters";
import { formatBaasConsoleConflictMessage } from "../../../../Services/baasConsoleClient";
import {
  DEFAULT_ENTITY_TOTALS,
  computeEntityTotals,
  fetchBaasEntities,
  fetchBaasEntitiesPage,
  fetchBaasPeerInstanceOwners,
  fetchBaasCarteiraPresence,
  isBaasEntityAwaitingFirstAccess,
  isValutMatrixConsolePin,
  resetBaasEntityTransactionalPassword,
  revokeBaasCarteiraSession,
} from "../../../../Services/baasEntidadesApi";
import { maskTaxIdForList } from "../../../../Utils/masks";
import {
  isBaasInstanceOwnerEntity,
  resolveBaasInstanceOwnerEntityId,
} from "../../../../Services/baasVirtualShells";
import {
  fetchBaasInstanceDetail,
  fetchBaasInstanceFeatures,
} from "../../../../Services/baasInstancesApi";
import {
  BAAS_LIST_FILTER_ALL,
  BAAS_LIST_FILTER_VALUT,
  canUseBaasGlobalInstanceFilter,
  rowMatchesBaasListInstanceFilter,
  shouldFetchBaasPeerLists,
  stampBaasRowInstance,
} from "../baasInstanceListFilter";
import { shouldPinPeerForEntityOps } from "../baasPeerOpsPin";
import {
  EMPTY_BAAS_ENTIDADES_FILTERS,
  filterBaasEntidades,
  hasActiveBaasEntidadesFilters,
} from "./baasEntidadesFilters";
import { ENTIDADES_CREATE_PATH } from "./BaasEntidadeCreatePage";
import { buildEntidadeDetailPath } from "./BaasEntidadeDetailPage";
import { ENTIDADES_PERSONAL_CREATE_PATH } from "./BaasEntidadePersonalCreatePage";
import { BAAS_ONBOARD_PATH } from "../constants";
import { BaasEntidadeKycGenerateModal } from "./components/BaasEntidadeKycGenerateModal";
import { BaasEntidadeTypeModal } from "./components/BaasEntidadeTypeModal";
import { BaasEntidadesAlertBanner } from "./components/BaasEntidadesAlertBanner";
import { BaasEntidadesFilterBar } from "./components/BaasEntidadesFilterBar";
import { BaasEntidadesQuickActions } from "./components/BaasEntidadesQuickActions";
import { BaasEntidadesTotalsSection } from "./components/BaasEntidadesTotalsSection";

/** Person (PF) rows only — same rule as entity-detail Gerar link. */
const isPersonEntityRow = (entity) =>
  entity?.type === "pf"
  || String(entity?.id || "").startsWith("pers_");

const statusStyles = {
  active: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
  pending: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
  suspended: "bg-gray-100 text-gray-600 dark:bg-brand-dark-elevated dark:text-gray-400",
  none: "bg-gray-100 text-gray-500 dark:bg-brand-dark-elevated dark:text-gray-400",
};

const kybStyles = {
  approved: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
  in_review: "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400",
  pending: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
  rejected: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400",
};

const formatDate = (value) => {
  if (!value) return "—";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "short" }).format(new Date(value));
};

const TARGET_KEY_BY_TOTAL = {
  total: "totalTarget",
  active: "activeTarget",
  pending: "pendingTarget",
  suspended: "suspendedTarget",
};

const isQueryTruthy = (value) =>
  value === true || value === "true" || value === "1" || value === "on";

const resolveCreateRedirectFromQuery = (search) => {
  const parsed = qs.parse(search.replace("?", ""));
  const jsonMode = parsed.create === "json" || isQueryTruthy(parsed.json);
  const shouldRedirect = jsonMode || isQueryTruthy(parsed.create) || parsed.create === "json";

  return { shouldRedirect, jsonMode };
};

export const BaasEntidadesPage = () => {
  const { addToast } = useToast();
  const location = useLocation();
  const history = useHistory();
  const { instances, selectedInstance, dataEpoch, listInstanceFilter, selectInstanceForPeerOps, clearPeerOpsPin } =
    useBaasInstance();

  // List view always uses the sidebar pin (Valut) — drop soft peer ops leftover.
  useEffect(() => {
    clearPeerOpsPin?.();
  }, [clearPeerOpsPin]);
  const { can, loading: capabilitiesLoading } = useBaasCapabilities();
  const { isValutConfigAdmin, canUseViewAs } = useBaasPlatformCreateAccess();
  const isValutAdmin = Boolean(isValutConfigAdmin || canUseViewAs);
  const canCreateEntity = can("entities.create");
  const canCreateInvites = can("entities.create");
  const isLoading = useBaasPageLoading(
    selectedInstance?.id
      ? `${selectedInstance.id}:${listInstanceFilter || "all"}:${dataEpoch}`
      : null,
  );
  const [filters, setFilters] = useState(EMPTY_BAAS_ENTIDADES_FILTERS);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editingTotals, setEditingTotals] = useState(false);
  const [targets, setTargets] = useState(DEFAULT_ENTITY_TOTALS);
  const [draftTargets, setDraftTargets] = useState(DEFAULT_ENTITY_TOTALS);
  const [alertDismissed, setAlertDismissed] = useState(false);
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [generateEntityId, setGenerateEntityId] = useState(null);
  const [inviteFeatureEnabled, setInviteFeatureEnabled] = useState(false);
  const [entities, setEntities] = useState([]);
  const [entitiesTotal, setEntitiesTotal] = useState(0);
  const [listMode, setListMode] = useState("server");
  const [entitiesLoading, setEntitiesLoading] = useState(false);
  const entitiesRef = useRef(entities);
  entitiesRef.current = entities;
  const [ownerEntityId, setOwnerEntityId] = useState(() =>
    resolveBaasInstanceOwnerEntityId(selectedInstance),
  );

  const createRedirect = useMemo(
    () => resolveCreateRedirectFromQuery(location.search),
    [location.search],
  );

  useEffect(() => {
    let cancelled = false;

    if (!selectedInstance) {
      setOwnerEntityId("");
      return undefined;
    }

    const fromSelection = resolveBaasInstanceOwnerEntityId(selectedInstance);
    if (fromSelection) {
      setOwnerEntityId(fromSelection);
      return undefined;
    }

    (async () => {
      try {
        const detail = await fetchBaasInstanceDetail(selectedInstance);
        if (!cancelled) {
          setOwnerEntityId(resolveBaasInstanceOwnerEntityId(detail));
        }
      } catch {
        if (!cancelled) setOwnerEntityId("");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedInstance, dataEpoch]);

  useEffect(() => {
    let cancelled = false;
    if (!selectedInstance?.baasInstanceId || !canCreateInvites) {
      setInviteFeatureEnabled(false);
      return undefined;
    }
    fetchBaasInstanceFeatures(selectedInstance)
      .then((features) => {
        if (cancelled) return;
        const list = Array.isArray(features) ? features : [];
        const invite = list.find(
          (item) =>
            String(item?.featureCode || "")
              .trim()
              .toLowerCase() === "kyc.invite_link",
        );
        setInviteFeatureEnabled(Boolean(invite?.enabled));
      })
      .catch(() => {
        if (!cancelled) setInviteFeatureEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedInstance, canCreateInvites, dataEpoch]);

  useEffect(() => {
    if (location.state?.entityCreated) {
      setRefreshKey((current) => current + 1);
      history.replace({ pathname: location.pathname, search: location.search });
      return;
    }

    if (location.state?.presetFilters) {
      setFilters((current) => ({
        ...current,
        ...location.state.presetFilters,
      }));
      setPage(1);
      setAlertDismissed(false);
      history.replace({ pathname: location.pathname, search: location.search });
    }
  }, [history, location.pathname, location.search, location.state]);

  const needsFullList =
    hasActiveBaasEntidadesFilters(filters)
    || (
      canUseBaasGlobalInstanceFilter(selectedInstance)
      && Boolean(listInstanceFilter)
      && listInstanceFilter !== BAAS_LIST_FILTER_ALL
      && listInstanceFilter !== BAAS_LIST_FILTER_VALUT
    );

  useEffect(() => {
    let cancelled = false;

    if (!selectedInstance) {
      setEntities([]);
      setEntitiesTotal(0);
      return undefined;
    }

    const useServerPage = !needsFullList;
    setEntitiesLoading(true);
    (async () => {
      try {
        const loadUnscoped = shouldFetchBaasPeerLists(
          selectedInstance,
          listInstanceFilter,
        );
        const loadEntities = (unscoped) =>
          useServerPage
            ? fetchBaasEntitiesPage(selectedInstance, {
              page,
              pageSize: rowsPerPage,
              reconcileContaWithAccounts: true,
              unscoped,
            })
            : fetchBaasEntities(selectedInstance, {
              reconcileContaWithAccounts: true,
              unscoped,
            }).then((items) => ({
              items: Array.isArray(items) ? items : [],
              total: Array.isArray(items) ? items.length : 0,
            }));
        const [scoped, peerOwners] = await Promise.all([
          loadUnscoped
            ? loadEntities(true).catch(() => loadEntities(false))
            : loadEntities(false),
          // Peer-owner badges only when header is "Todas" / a peer (same as Contas).
          loadUnscoped
            ? fetchBaasPeerInstanceOwners(selectedInstance)
            : Promise.resolve([]),
        ]);
        const byId = new Map();
        for (const entity of Array.isArray(scoped?.items) ? scoped.items : []) {
          if (entity?.id) {
            byId.set(entity.id, stampBaasRowInstance(entity, selectedInstance));
          }
        }
        // Peer-owner rows win (instance badge). On a server page, extra owners
        // only land on page 1 so later pages stay one Baas page.
        const peerList = Array.isArray(peerOwners) ? peerOwners : [];
        for (const entity of peerList) {
          if (!entity?.id) continue;
          if (byId.has(entity.id)) {
            byId.set(entity.id, { ...byId.get(entity.id), ...entity });
          } else if (!useServerPage || page === 1) {
            byId.set(entity.id, entity);
          }
        }
        let next = [...byId.values()];
        if (isValutMatrixConsolePin(selectedInstance)) {
          try {
            const personIds = next.map((entity) => entity.personId).filter(Boolean);
            const logged = await fetchBaasCarteiraPresence(personIds);
            next = next.map((entity) => ({
              ...entity,
              loggedInCarteira: Boolean(
                entity.personId && logged.has(Number(entity.personId)),
              ),
            }));
          } catch {
            // Presence is best-effort — list still renders.
          }
        }
        if (!cancelled) {
          setEntities(next);
          setEntitiesTotal(Number(scoped?.total) || next.length);
          setListMode(useServerPage ? "server" : "full");
        }
      } catch {
        if (!cancelled) {
          setEntities([]);
          setEntitiesTotal(0);
        }
      } finally {
        if (!cancelled) setEntitiesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    selectedInstance,
    dataEpoch,
    listInstanceFilter,
    needsFullList,
    needsFullList ? "full" : `${page}:${rowsPerPage}`,
    refreshKey,
  ]);

  // Presence dots: poll SignalR online set every 12s (matrix pin only).
  useEffect(() => {
    if (!selectedInstance || !isValutMatrixConsolePin(selectedInstance)) {
      return undefined;
    }
    let cancelled = false;
    const tick = async () => {
      try {
        const personIds = entitiesRef.current
          .map((entity) => entity.personId)
          .filter(Boolean);
        if (personIds.length === 0) return;
        const logged = await fetchBaasCarteiraPresence(personIds);
        if (cancelled) return;
        setEntities((current) =>
          current.map((entity) => ({
            ...entity,
            loggedInCarteira: Boolean(
              entity.personId && logged.has(Number(entity.personId)),
            ),
          })),
        );
      } catch {
        // best-effort
      }
    };
    const timer = setInterval(tick, 12_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [selectedInstance]);

  const instanceNameById = useMemo(() => {
    const map = {};
    for (const instance of instances) {
      if (instance?.id) map[instance.id] = instance.name;
      const numericId = instance?.baasInstanceId ?? instance?.instanceId;
      if (numericId != null && numericId !== "") {
        map[numericId] = instance.name;
        map[String(numericId)] = instance.name;
      }
    }
    return map;
  }, [instances]);

  const clientEntities = useMemo(
    () =>
      entities.filter(
        (entity) =>
          entity?.isPeerInstanceOwner === true ||
          !isBaasInstanceOwnerEntity(entity, ownerEntityId),
      ),
    [entities, ownerEntityId],
  );

  const showInstanceColumn = useMemo(
    () =>
      canUseBaasGlobalInstanceFilter(selectedInstance)
      || clientEntities.some(
        (entity) => entity?.isPeerInstanceOwner || entity?.instanceName,
      ),
    [selectedInstance, clientEntities],
  );

  const filteredItems = useMemo(
    () =>
      filterBaasEntidades(clientEntities, filters).filter((entity) =>
        rowMatchesBaasListInstanceFilter(
          entity,
          listInstanceFilter,
          selectedInstance,
          instances,
        ),
      ),
    [clientEntities, filters, listInstanceFilter, selectedInstance, instances],
  );

  const totals = useMemo(() => {
    const computed = computeEntityTotals(
      clientEntities.filter((entity) =>
        rowMatchesBaasListInstanceFilter(
          entity,
          listInstanceFilter,
          selectedInstance,
          instances,
        ),
      ),
    );
    if (listMode === "server") {
      return { ...computed, total: entitiesTotal };
    }
    return computed;
  }, [clientEntities, listInstanceFilter, selectedInstance, instances, listMode, entitiesTotal]);
  const exceededTargets = useMemo(() => {
    return Object.entries(TARGET_KEY_BY_TOTAL)
      .filter(([totalKey, targetKey]) => totals[totalKey] > (targets[targetKey] ?? 0))
      .map(([totalKey]) => totalKey);
  }, [totals, targets]);

  const paginatedItems = useMemo(
    () => (listMode === "server" ? filteredItems : paginateList(filteredItems, page, rowsPerPage)),
    [filteredItems, page, rowsPerPage, listMode],
  );

  const isValutMatrixEntidades = isValutMatrixConsolePin(selectedInstance);

  const handleCarteiraLogout = async (entity) => {
    if (!entity?.personId || !entity.loggedInCarteira) return;
    if (entity.type === "pj" || entity.isPeerInstanceOwner) return;
    const confirmed = window.confirm(
      intl.get("baas.entidades.logoutConfirm", { name: entity.name || "" }),
    );
    if (!confirmed) return;
    try {
      await revokeBaasCarteiraSession(entity.personId);
      addToast({
        type: "success",
        title: intl.get("baas.entidades.logoutSuccess"),
      });
      setRefreshKey((current) => current + 1);
    } catch (err) {
      addToast({
        type: "error",
        title: intl.get("baas.entidades.logoutFailed"),
        description: formatBaasConsoleConflictMessage(
          err,
          err?.detail || err?.message || null,
        ),
      });
    }
  };

  const handleResetTransactionalPassword = async (entity) => {
    if (!entity?.personId) return;
    if (entity.type === "pj" || entity.isPeerInstanceOwner) return;
    if (isBaasEntityAwaitingFirstAccess(entity)) return;
    const confirmed = window.confirm(
      intl.get("baas.entidades.txPasswordResetConfirm", { name: entity.name || "" }),
    );
    if (!confirmed) return;
    try {
      await resetBaasEntityTransactionalPassword(entity.personId);
      addToast({
        type: "success",
        title: intl.get("baas.entidades.txPasswordResetSuccess"),
      });
    } catch (err) {
      addToast({
        type: "error",
        title: intl.get("baas.entidades.txPasswordResetFailed"),
        description: err?.code === "first_access_incomplete"
          || err?.code === "transactional_password_not_set"
          ? intl.get("baas.entidades.firstAccessRequiredHint")
          : formatBaasConsoleConflictMessage(
            err,
            err?.detail || err?.message || null,
          ),
      });
    }
  };

  const handleFiltersChange = (nextFilters) => {
    setFilters(nextFilters);
    setPage(1);
    setAlertDismissed(false);
  };

  const handleSaveTargets = () => {
    setTargets({ ...draftTargets });
    setEditingTotals(false);
    setAlertDismissed(false);
    addToast({
      type: "success",
      title: intl.get("baas.entidades.totalsSaved"),
    });
  };

  const handleViewPending = () => {
    handleFiltersChange({
      ...filters,
      statuses: ["pending"],
    });
  };

  const handleViewPendingKyb = () => {
    handleFiltersChange({
      ...filters,
      kybStatuses: ["pending", "in_review"],
    });
  };

  if (isLoading || entitiesLoading) {
    return <BaasEntidadesPageSkeleton />;
  }

  if (createRedirect.shouldRedirect && !capabilitiesLoading) {
    if (!can("entities.create")) {
      return <Redirect to={location.pathname} />;
    }

    const search = createRedirect.jsonMode ? "?json=on" : "";
    return <Redirect to={`${ENTIDADES_CREATE_PATH}${search}`} />;
  }

  return (
    <div className="baas-entidades min-h-0 min-w-0 h-auto space-y-5">
      <header className="min-w-0 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-brand-dark dark:text-gray-100">
            {intl.get("baas.nav.entities")}
            {selectedInstance?.name ? (
              <span className="font-semibold text-gray-400 dark:text-gray-500">
                {" "}
                — {selectedInstance.name}
              </span>
            ) : null}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.entidades.subtitle")}
          </p>
        </div>

        {canCreateEntity ? (
          <button
            type="button"
            className="btn-primary shrink-0 self-start"
            onClick={() => setTypeModalOpen(true)}
          >
            <TbBuildingBank size={18} aria-hidden />
            {intl.get("baas.entidades.create.button")}
          </button>
        ) : null}
      </header>

      <BaasEntidadesAlertBanner
        pendingKybCount={totals.pendingKyb}
        pendingCount={totals.pending}
        exceededTargets={exceededTargets}
        dismissed={alertDismissed}
        onDismiss={() => setAlertDismissed(true)}
        onViewPending={handleViewPending}
        onViewPendingKyb={handleViewPendingKyb}
      />

      <BaasEntidadesQuickActions
        createEntityEnabled={canCreateEntity}
        onboardLinksEnabled={canCreateInvites && inviteFeatureEnabled}
        onCreateEntityClick={() => setTypeModalOpen(true)}
      />

      <BaasEntidadesTotalsSection
        totals={totals}
        targets={editingTotals ? draftTargets : targets}
        editing={editingTotals}
        onToggleEdit={() => {
          setDraftTargets({ ...targets });
          setEditingTotals(true);
        }}
        onTargetChange={(key, value) => setDraftTargets((current) => ({ ...current, [key]: value }))}
        onSave={handleSaveTargets}
        onCancel={() => {
          setDraftTargets({ ...targets });
          setEditingTotals(false);
        }}
      />

      <BaasEntidadesFilterBar
        filters={filters}
        onFiltersChange={handleFiltersChange}
        filteredItems={filteredItems}
      />

      <GridHistoryCard>
        <div className="h-auto min-h-0 overflow-x-auto">
          <table className="h-auto min-w-full text-left text-sm">
            <thead className="border-b border-gray-100 bg-gray-50/80 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:border-brand-dark-border dark:bg-brand-dark-elevated/50 dark:text-gray-400">
              <tr>
                <th className="px-4 py-3">{intl.get("baas.entidades.columns.name")}</th>
                <th className="px-4 py-3">{intl.get("baas.entidades.columns.document")}</th>
                <th className="px-4 py-3">{intl.get("baas.entidades.columns.type")}</th>
                <th className="px-4 py-3">{intl.get("baas.entidades.columns.status")}</th>
                <th className="px-4 py-3">{intl.get("baas.entidades.columns.kyb")}</th>
                {showInstanceColumn ? (
                  <th className="px-4 py-3">{intl.get("baas.entidades.columns.instance")}</th>
                ) : null}
                <th className="px-4 py-3">{intl.get("baas.entidades.columns.createdAt")}</th>
                <th className="px-4 py-3">{intl.get("baas.entidades.columns.actions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-brand-dark-border">
              {paginatedItems.length === 0 ? (
                <tr>
                  <td
                    colSpan={showInstanceColumn ? 8 : 7}
                    className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                  >
                    {intl.get("baas.entidades.empty")}
                  </td>
                </tr>
              ) : (
                paginatedItems.map((entity) => {
                  const openDetail = () => {
                    if (shouldPinPeerForEntityOps(entity, selectedInstance)) {
                      selectInstanceForPeerOps?.(
                        entity.instanceId ?? entity.baasInstanceId,
                        selectedInstance?.environment,
                      );
                    }
                    history.push(buildEntidadeDetailPath(entity.id));
                  };
                  const handleRowKeyDown = (event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      openDetail();
                    }
                  };
                  const isPersonCorrentista = isPersonEntityRow(entity);
                  const showCarteiraLogout =
                    isValutMatrixEntidades
                    && !entity.isPeerInstanceOwner
                    && isPersonCorrentista
                    && entity.type !== "pj"
                    && Boolean(entity.personId);
                  const showTxPasswordReset = isValutAdmin && showCarteiraLogout;
                  // Public onboard = same Gerar link modal as entity detail (PF only).
                  const showPublicOnboard =
                    canCreateInvites
                    && isPersonCorrentista
                    && entity.type !== "pj"
                    && !entity.isPeerInstanceOwner
                    && !String(entity.id || "").startsWith("enti_");

                  const rowMenuItems = [
                    {
                      id: "details",
                      label: intl.get("common.details"),
                      onClick: openDetail,
                    },
                    showPublicOnboard
                      ? {
                          id: "public-onboard",
                          label: intl.get("baas.entidades.detail.verifications.generateButton"),
                          onClick: () => {
                            setGenerateEntityId(entity.id);
                            setGenerateOpen(true);
                          },
                        }
                      : null,
                    showCarteiraLogout
                      ? {
                          id: "logout",
                          label: intl.get("baas.entidades.logout"),
                          onClick: () => {
                            void handleCarteiraLogout(entity);
                          },
                          disabled: !entity.loggedInCarteira,
                        }
                      : null,
                    showTxPasswordReset
                      ? {
                          id: "tx-password-reset",
                          label: intl.get("baas.entidades.txPasswordReset"),
                          disabled: isBaasEntityAwaitingFirstAccess(entity),
                          title: isBaasEntityAwaitingFirstAccess(entity)
                            ? intl.get("baas.entidades.firstAccessRequiredHint")
                            : undefined,
                          onClick: () => {
                            void handleResetTransactionalPassword(entity);
                          },
                        }
                      : null,
                  ].filter(Boolean);

                  return (
                    <tr
                      key={entity.id}
                      className="cursor-pointer hover:bg-gray-50/60 dark:hover:bg-brand-dark-elevated/30"
                      role="button"
                      tabIndex={0}
                      onClick={openDetail}
                      onKeyDown={handleRowKeyDown}
                    >
                      <td className="px-4 py-3 font-medium uppercase text-brand-dark dark:text-gray-100">
                        <span className="inline-flex items-center gap-2">
                          {isValutMatrixEntidades && !entity.isPeerInstanceOwner && isPersonCorrentista ? (
                            <BaasLoggedInDot
                              loggedIn={Boolean(entity.loggedInCarteira)}
                              hasAccount={entity.hasAccount === true}
                              accessStatus={entity.accessStatus}
                              titleKey="baas.entidades.loggedInCarteira"
                              offlineTitleKey="baas.entidades.loggedOutCarteira"
                              noAccountTitleKey="baas.entidades.noAccountCarteira"
                            />
                          ) : null}
                          {entity.name}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-600 dark:text-gray-300">
                        {maskTaxIdForList(entity.document)}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                        {intl.get(`baas.entidades.type.${entity.type}`)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${statusStyles[entity.status] ?? ""}`}
                          title={intl.get("baas.entidades.columns.status")}
                        >
                          {intl.get(`baas.entidades.status.${entity.status}`)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${kybStyles[entity.kybStatus] ?? ""}`}
                          title={intl.get("baas.entidades.columns.kyb")}
                        >
                          {intl.get(`baas.entidades.kybStatus.${entity.kybStatus}`)}
                        </span>
                      </td>
                      {showInstanceColumn ? (
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                          {entity.instanceName
                            ?? instanceNameById[entity.instanceId]
                            ?? entity.instanceId
                            ?? "—"}
                        </td>
                      ) : null}
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                        {formatDate(entity.createdAt)}
                      </td>
                      <td
                        className="px-4 py-3"
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => event.stopPropagation()}
                      >
                        <BaasRowActionMenu items={rowMenuItems} />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <GridPagination
          total={listMode === "server" ? entitiesTotal : filteredItems.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={(value) => {
            setRowsPerPage(value);
            setPage(1);
          }}
          currentPageCount={paginatedItems.length}
          itemLabel={intl.get("baas.entidades.paginationLabel")}
        />
      </GridHistoryCard>

      <BaasEntidadeTypeModal
        open={typeModalOpen}
        onClose={() => setTypeModalOpen(false)}
        showPersonalLinkOption={isValutMatrixEntidades}
        onSelectPersonal={() => {
          setTypeModalOpen(false);
          history.push(ENTIDADES_PERSONAL_CREATE_PATH);
        }}
        onSelectPersonalLink={() => {
          setTypeModalOpen(false);
          setGenerateEntityId(null);
          setGenerateOpen(true);
        }}
        onSelectBusiness={() => {
          setTypeModalOpen(false);
          history.push(ENTIDADES_CREATE_PATH);
        }}
      />

      <BaasEntidadeKycGenerateModal
        open={generateOpen}
        onClose={() => {
          setGenerateOpen(false);
          setGenerateEntityId(null);
        }}
        instance={selectedInstance}
        entityId={generateEntityId}
        canCreateInvite={canCreateInvites}
        inviteFeatureEnabled={inviteFeatureEnabled}
        onViewAllLinks={() => {
          setGenerateOpen(false);
          setGenerateEntityId(null);
          history.push(BAAS_ONBOARD_PATH);
        }}
      />

    </div>
  );
};
